import type { z } from "zod";

import type { formFieldsSchema } from "@/schemas/forms";

type FieldDefinition = z.infer<(typeof formFieldsSchema)[number]>;

export type EligibilityGate = Extract<
  FieldDefinition,
  { _type: "eligibilityNote" }
>;

/** Minimal shape of a form field list (top level or inside a fieldset). */
interface FieldLike {
  _type: string;
  fields?: FieldLike[];
}

/** Finds the form's `eligibilityNote` field, including inside fieldsets. */
export function findEligibilityGate(
  fields: readonly FieldLike[]
): EligibilityGate | undefined {
  for (const field of fields) {
    if (field._type === "eligibilityNote") {
      return field as unknown as EligibilityGate;
    }
    if (field.fields) {
      const nested = findEligibilityGate(field.fields);
      if (nested) return nested;
    }
  }
  return undefined;
}

/**
 * True when a value has been chosen and it is not one of the eligible values.
 * An empty / unselected value is *not* ineligible, so nothing shows before the
 * visitor has picked a country.
 */
export function isIneligible(
  gate: Pick<EligibilityGate, "eligibleValues">,
  value: unknown
): boolean {
  return (
    typeof value === "string" &&
    value.trim() !== "" &&
    !gate.eligibleValues.includes(value)
  );
}

/**
 * The URL to send an ineligible visitor to, with UTMs.
 *
 * Source, medium and campaign are only filled in when the visitor didn't
 * arrive with their own (so paid-ad attribution survives); the content UTM is
 * always set so these visits are identifiable. `search` is the landing page's
 * query string. `values` are the form's current field values; those named in
 * the redirect's `prefill` map are added as query parameters when non-empty.
 */
export function buildIneligibleRedirectUrl(
  gate: Pick<EligibilityGate, "ineligibleRedirect">,
  search = "",
  values: Record<string, unknown> = {}
): string | null {
  const redirect = gate.ineligibleRedirect;
  if (!redirect) return null;

  const url = new URL(redirect.url);
  const incoming = new URLSearchParams(search);
  for (const key of ["source", "medium", "campaign"] as const) {
    const param = `utm_${key}`;
    if (!url.searchParams.has(param)) {
      url.searchParams.set(param, incoming.get(param) ?? redirect.utm[key]);
    }
  }
  url.searchParams.set("utm_content", redirect.utm.content);
  for (const [field, param] of Object.entries(redirect.prefill ?? {})) {
    const value = values[field];
    if (typeof value === "string" && value.trim() !== "") {
      url.searchParams.set(param, value.trim());
    }
  }
  return url.toString();
}
